import type { ActivityContext, ActivityHandle } from '../../core/activity';
import { makeDraggable } from '../../shared/drag-drop';
import levelsData from './levels.json';
import '../ordering_numbers/style.css';

type Mode = 'ascending' | 'descending';

interface RoundConfig {
  mode: Mode;
  values?: string[];
  random?: boolean;
  windowSize?: number;
  count?: number;
}

interface Level {
  level: number;
  objective: string;
  difficulty: number;
  data: RoundConfig[];
}

const LEVELS = (levelsData as { levels: Level[] }).levels;
const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

interface PoolItem {
  value: string;
  placed: boolean;
  wrongFlash: boolean;
}

export function mountOrderingAlphabets(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const cfg = level.data[0]!;
  const mode = cfg.mode;
  const letras = sorteiaLetras(cfg);
  const targetOrder = mode === 'ascending'
    ? [...letras].sort((a, b) => a.localeCompare(b))
    : [...letras].sort((a, b) => b.localeCompare(a));

  const pool: PoolItem[] = shuffle(letras.map((v) => ({ value: v, placed: false, wrongFlash: false })));
  const ordered: string[] = [];

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Ordenar letras</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage ordering">
      <div class="feedback" aria-live="polite"></div>
      <p class="ordering__mode">${mode === 'ascending' ? 'Ordem do alfabeto (A → Z)' : 'Ordem contrária (Z → A)'}</p>
      <p class="ordering__hint" aria-live="polite"></p>
      <div class="ordering__sequence" role="list" aria-label="Sequência ordenada"></div>
      <div class="ordering__pool" role="list" aria-label="Letras embaralhadas"></div>
    </section>
  `;
  ctx.root.appendChild(page);

  const sequenceEl = page.querySelector<HTMLElement>('.ordering__sequence')!;
  const poolEl = page.querySelector<HTMLElement>('.ordering__pool')!;
  const hintEl = page.querySelector<HTMLElement>('.ordering__hint')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());

  function renderAll(): void {
    renderSequence();
    renderPool();
    renderHint();
  }

  function renderSequence(): void {
    sequenceEl.innerHTML = '';
    for (let i = 0; i < targetOrder.length; i++) {
      const slot = document.createElement('div');
      slot.className = 'ordering__slot';
      if (i < ordered.length) {
        slot.classList.add('ordering__slot--filled');
        slot.textContent = ordered[i]!;
      } else {
        slot.textContent = '?';
        if (i === ordered.length) {
          slot.classList.add('ordering__slot--next');
          slot.dataset['dropNext'] = '1';
        }
      }
      sequenceEl.appendChild(slot);
    }
  }

  function renderPool(): void {
    poolEl.innerHTML = '';
    pool.forEach((item, i) => {
      if (item.placed) return;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'ordering__num';
      el.dataset.idx = String(i);
      el.textContent = item.value;
      if (item.wrongFlash) el.classList.add('ordering__num--wrong');
      el.addEventListener('click', () => clickLetra(i));
      poolEl.appendChild(el);
      makeDraggable(el, {
        getDropTarget: (x, y) => {
          const e = document.elementFromPoint(x, y);
          return e?.closest<HTMLElement>('[data-drop-next]') ?? null;
        },
        onDrop: () => clickLetra(i),
      });
    });
  }

  function renderHint(): void {
    if (ordered.length === targetOrder.length) {
      hintEl.textContent = '';
      return;
    }
    const qual = mode === 'ascending' ? 'que vem primeiro no alfabeto' : 'que vem por último no alfabeto';
    hintEl.textContent = ordered.length === 0
      ? `Clique na letra ${qual}.`
      : `Próxima: a ${qual} entre as que sobraram.`;
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function clickLetra(idx: number): void {
    const item = pool[idx]!;
    if (item.placed) return;
    const expected = targetOrder[ordered.length];
    if (expected === undefined) return;
    if (item.value === expected) {
      item.placed = true;
      ordered.push(item.value);
      ctx.audio.play('correct');
      renderAll();
      if (ordered.length === targetOrder.length) {
        window.setTimeout(finishLevel, 600);
      }
    } else {
      item.wrongFlash = true;
      ctx.audio.play('wrong');
      showFeedback('err');
      renderPool();
      window.setTimeout(() => {
        item.wrongFlash = false;
        renderPool();
      }, 500);
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, ordered.length);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você ordenou ${ordered.length} letras.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/ordering_alphabets/${level.level + 1}`;
      });
    }
  }

  renderAll();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}

/** Sorteia letras: janela contígua do alfabeto e amostra sem repetição dentro dela. */
function sorteiaLetras(cfg: RoundConfig): string[] {
  if (!cfg.random) return cfg.values ? [...cfg.values] : [];
  const janela = Math.min(cfg.windowSize ?? 8, ALFABETO.length);
  const n = Math.min(cfg.count ?? 5, janela);
  const inicio = Math.floor(Math.random() * (ALFABETO.length - janela + 1));
  const candidatas = ALFABETO.slice(inicio, inicio + janela);
  return shuffle([...candidatas]).slice(0, n);
}

function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
  }
  return arr;
}
